/**
 * Financial Math Utilities
 * Helper functions for financial calculations focused on bar sales
 */

import { formatDateForInput } from "./calendarDate.js";
import { compareCivilYmd, parseCivilDateOnly } from "./civilDateTime.js";

const toYmd = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return parseCivilDateOnly(value);
  if (value instanceof Date) return formatDateForInput(value);
  return "";
};

/**
 * Calculate total sales from earnings (sales field only, ignoring tips/base_pay)
 * @param {Array|Object} earnings - Single earnings object or array of earnings
 * @returns {number} - Total sales amount
 */
export const calculateSalesTotal = (earnings) => {
  if (!earnings) return 0;

  if (Array.isArray(earnings)) {
    return earnings.reduce((total, earning) => {
      return total + (earning.sales || 0);
    }, 0);
  }

  return earnings.sales || 0;
};

/**
 * Format currency value
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Filter shows by date range (for shows with earnings data attached)
 * @param {Array} shows - Array of shows with date and earnings data (sales, base_pay, etc.)
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Array} - Filtered shows array
 */
export const filterShowsByDateRange = (shows, startDate, endDate) => {
  if (!shows || !Array.isArray(shows)) return [];
  if (!startDate || !endDate) return shows;

  const startYmd = toYmd(startDate);
  const endYmd = toYmd(endDate);
  if (!startYmd || !endYmd) return shows;

  return shows.filter((show) => {
    const showYmd = parseCivilDateOnly(show.date);
    if (!showYmd) return false;
    return (
      compareCivilYmd(showYmd, startYmd) >= 0 &&
      compareCivilYmd(showYmd, endYmd) <= 0
    );
  });
};

/**
 * Calculate total base pay from shows (base_pay field only)
 * @param {Array} shows - Array of shows with base_pay field
 * @returns {number} - Total base pay amount
 */
export const calculateBasePayTotal = (shows) => {
  if (!shows || !Array.isArray(shows)) return 0;
  return shows.reduce((total, show) => {
    return total + (show.base_pay || 0);
  }, 0);
};
